import React from 'react';
import { Box, Container, Grid, GridItem, Heading, Text, Icon } from '@chakra-ui/react';
import { Building2, Lock, ShieldCheck, UserRoundCheck } from 'lucide-react';
import SectionHeading from '../ui/SectionHeading';
import { useStaggeredAnimation } from '../../hooks/useScrollAnimation';

const ITEMS = [
  {
    icon: ShieldCheck,
    title: 'Governance & Oversight',
    description: 'Structured with clear internal controls, documentation, and investor reporting standards.',
  },
  {
    icon: UserRoundCheck,
    title: 'KYC & AML Verified',
    description: 'Rigorous identity verification and anti-money laundering processes.',
  },
  {
    icon: Lock,
    title: 'Secure Transactions',
    description: 'Bank-level encryption and multi-layer security for all transactions.',
  },
  {
    icon: Building2,
    title: 'Asset-Backed',
    description: 'All investments are backed by real, income-generating properties.',
  },
];

const InlineItem = ({ icon, title, description, isFirst, animationStyle }) => (
  <Box
    role="group"
    h="100%"
    pl={{ base: 0, lg: 6 }}
    pr={{ base: 0, lg: 0 }}
    borderLeft={{ base: 'none', lg: '1px solid rgba(255,255,255,0.06)' }}
    pt={{ base: isFirst ? 0 : 6, lg: 0 }}
    borderTop={{ base: isFirst ? 'none' : '1px solid rgba(255,255,255,0.06)', lg: 'none' }}
    style={animationStyle}
  >
    <Box
      w="44px"
      h="44px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="10px"
      bg="rgba(212,168,95,0.08)"
      border="1px solid"
      borderColor="rgba(212,168,95,0.14)"
      mb={4}
      transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
      _groupHover={{
        bg: 'rgba(212,168,95,0.16)',
        borderColor: 'rgba(212,168,95,0.32)',
      }}
    >
      <Icon as={icon} boxSize={5} color="#D4A85F" strokeWidth={1.7} />
    </Box>
    <Box
      h="1px"
      w="24px"
      bg="linear-gradient(90deg, #D4A85F 0%, transparent 100%)"
      mb={2.5}
      transition="width 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
      _groupHover={{ w: '40px' }}
    />
    <Heading
      as="h3"
      fontFamily="heading"
      fontSize={{ base: '1.2rem', md: '1.35rem' }}
      fontWeight={500}
      color="#F5F7FA"
      mb={2}
      letterSpacing="0"
      lineHeight={1.12}
      transition="color 0.25s ease"
      _groupHover={{ color: '#E0B873' }}
    >
      {title}
    </Heading>
    <Text
      fontFamily="body"
      fontSize="sm"
      color="#9AA7B8"
      lineHeight={1.6}
    >
      {description}
    </Text>
  </Box>
);

const ComplianceSecuritySection = () => {
  const [gridRef, visibleItems] = useStaggeredAnimation(ITEMS.length, {
    staggerDelay: 80,
    threshold: 0.15,
  });

  return (
    <Box as="section" py={{ base: 5, md: 5 }} bg="#071326">
      <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
        <Box
          bg="#0A1B34"
          border="1px solid"
          borderColor="rgba(255,255,255,0.10)"
          borderRadius="8px"
          px={{ base: 5, md: 7 }}
          py={{ base: 6, md: 7 }}
        >
          <Grid
            ref={gridRef}
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: '3fr 2.25fr 2.25fr 2.25fr 2.25fr',
            }}
            gap={{ base: 8, md: 8, lg: 8, xl: 10 }}
            alignItems="start"
          >
            <GridItem>
              <SectionHeading
                align="left"
                size="lg"
                title={<>Compliance<br />&amp; Security</>}
                subtitle="Your security and peace of mind are our top priorities."
                mb={0}
              />
            </GridItem>

            {ITEMS.map((it, index) => (
              <GridItem key={it.title}>
                <InlineItem
                  icon={it.icon}
                  title={it.title}
                  description={it.description}
                  isFirst={index === 0}
                  animationStyle={{
                    opacity: visibleItems.has(index) ? 1 : 0,
                    transform: visibleItems.has(index) ? 'translateY(0)' : 'translateY(12px)',
                    transition:
                      'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
              </GridItem>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ComplianceSecuritySection;
